"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRightIcon, XIcon } from "lucide-react";

import type { ImageDto } from "@tripcart/shared";

import { Button } from "@tripcart/traveler-ui/components/button";
import { cx } from "@tripcart/traveler-ui/cx";

import { usePermalinkPath } from "@/lib/permalinks-context";
import { resetScrollOnNavigate } from "@/lib/reset-scroll-on-navigate";
import { usePluralTranslator, useTranslator } from "@/lib/translation-context";

import styles from "./_destinations-grid.client.module.css";

export interface DestinationEntry {
  slug: string;
  name: string;
  /** Optional — operator may not have uploaded a featured image. The
   *  cards render a brand-tinted placeholder when this is undefined. */
  hero?: ImageDto;
  tourCount: number;
  parentSlug: string | null;
  parentName: string | null;
  regionCount: number;
}

/**
 * Image (or placeholder) inside a card. Centralised so both the
 * top-level card and the region mini-card render hero/placeholder
 * identically.
 *
 * The placeholder stays asset-free (pure CSS + one inline SVG — no network
 * request, nothing to lazy-load) but is no longer near-invisible: at
 * 15%/8%/5% brand tint it read as an empty grey rectangle, so an agency that
 * hadn't uploaded destination photos looked like a page that had failed to
 * load rather than one awaiting content. A deeper brand wash plus a low-
 * contrast ridgeline motif makes an image-less card look deliberate.
 */
function DestinationImage({
  hero,
  name,
}: {
  hero?: ImageDto;
  name: string;
}): React.ReactElement {
  if (hero) {
    return (
      <Image
        src={hero.url}
        alt={hero.alt || name}
        fill
        sizes="(min-width: 1100px) 25vw, (min-width: 760px) 33vw, 50vw"
        className={styles.image}
        placeholder={hero.blurDataURL ? "blur" : "empty"}
        blurDataURL={hero.blurDataURL}
      />
    );
  }
  return (
    <div aria-hidden className={styles.placeholder}>
      {/* Ridgeline motif — inline, so it costs no request and no CLS.
          `currentColor` keeps it on the tenant's own primary. */}
      <svg
        viewBox="0 0 120 60"
        fill="none"
        preserveAspectRatio="xMidYMax meet"
        className={styles.ridgeline}
      >
        <path
          d="M0 60 L30 26 L46 42 L70 12 L92 38 L104 28 L120 60 Z"
          fill="currentColor"
        />
        <path
          d="M0 60 L22 40 L40 52 L58 34 L78 50 L98 40 L120 60 Z"
          fill="currentColor"
          opacity="0.55"
        />
      </svg>
    </div>
  );
}

interface DestinationsGridProps {
  entries: DestinationEntry[];
}

function useColumnCount(ref: React.RefObject<HTMLDivElement | null>): number {
  const [cols, setCols] = useState(4);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = (): void => {
      const template = window.getComputedStyle(el).gridTemplateColumns;
      const count = template
        .split(" ")
        .filter((s) => s.trim().length > 0).length;
      if (count > 0) setCols(count);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return cols;
}

function RegionsToggleButton({
  entry,
  isOpen,
  onToggle,
}: {
  entry: DestinationEntry;
  isOpen: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const tn = usePluralTranslator();
  return (
    <Button
      variant="link"
      size="sm"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={`expand-${entry.slug}`}
    >
      {tn("{count} region", "{count} regions", entry.regionCount, {
        count: entry.regionCount,
      })}
      <ChevronRightIcon
        className={cx(styles.chevron, isOpen && styles.chevronOpen)}
        strokeWidth={2.5}
      />
    </Button>
  );
}

function DestinationCard({
  entry,
  isOpen,
  onToggleRegions,
}: {
  entry: DestinationEntry;
  isOpen: boolean;
  onToggleRegions?: () => void;
}): React.ReactElement {
  const t = useTranslator();
  const tn = usePluralTranslator();
  const path = usePermalinkPath();
  return (
    <div className={styles.card}>
      <Link
        href={path("destination", entry.slug)}
        className={styles.cardLink}
        aria-label={t("View {name}", { name: entry.name })}
        scroll={false}
        onClick={resetScrollOnNavigate}
      >
        <div className={styles.media}>
          <DestinationImage hero={entry.hero} name={entry.name} />
          <span className={styles.tourCount}>
            {tn("{count} tour", "{count} tours", entry.tourCount, {
              count: entry.tourCount,
            })}
          </span>
        </div>
      </Link>
      <div className={styles.footer}>
        <Link
          href={path("destination", entry.slug)}
          className={styles.name}
          scroll={false}
          onClick={resetScrollOnNavigate}
        >
          {entry.name}
          {entry.parentName && (
            <span className={styles.parentName}> ({entry.parentName})</span>
          )}
        </Link>
        {onToggleRegions && (
          <RegionsToggleButton
            entry={entry}
            isOpen={isOpen}
            onToggle={onToggleRegions}
          />
        )}
      </div>
    </div>
  );
}

function RegionMiniCard({
  entry,
}: {
  entry: DestinationEntry;
}): React.ReactElement {
  const t = useTranslator();
  const tn = usePluralTranslator();
  const path = usePermalinkPath();
  return (
    <div className={styles.card}>
      <Link
        href={path("destination", entry.slug)}
        className={styles.cardLink}
        aria-label={t("View {name}", { name: entry.name })}
        scroll={false}
        onClick={resetScrollOnNavigate}
      >
        <div className={cx(styles.media, styles.mediaRegion)}>
          <DestinationImage hero={entry.hero} name={entry.name} />
          <span className={styles.tourCount}>
            {tn("{count} tour", "{count} tours", entry.tourCount, {
              count: entry.tourCount,
            })}
          </span>
        </div>
        <p className={styles.regionName}>{entry.name}</p>
      </Link>
    </div>
  );
}

function ExpandPanel({
  parentName,
  regions,
  onClose,
  panelId,
}: {
  parentName: string;
  regions: DestinationEntry[];
  onClose: () => void;
  panelId: string;
}): React.ReactElement {
  const t = useTranslator();
  return (
    <div id={panelId} className={styles.panel} style={{ gridColumn: "1 / -1" }}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>
          {t("Regions of {name}", { name: parentName })}
        </span>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <XIcon strokeWidth={2} />
          {t("Close")}
        </Button>
      </div>
      <div className={styles.regionsGrid}>
        {regions.map((c) => (
          <RegionMiniCard key={c.slug} entry={c} />
        ))}
      </div>
    </div>
  );
}

function getAllDescendants(
  entries: DestinationEntry[],
  parentSlug: string,
  seen: Set<string> = new Set(),
): DestinationEntry[] {
  const direct = entries.filter(
    (e) => e.parentSlug === parentSlug && !seen.has(e.slug),
  );
  return direct.flatMap((child) => {
    seen.add(child.slug);
    return [child, ...getAllDescendants(entries, child.slug, seen)];
  });
}

type RenderItem =
  | { kind: "card"; entry: DestinationEntry }
  | { kind: "panel"; parentSlug: string };

export function DestinationsGrid({
  entries,
}: DestinationsGridProps): React.ReactElement {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const cols = useColumnCount(gridRef);

  // Descendants of the open parent are pulled out of the grid and shown
  // in the expand panel. By default (nothing open) everything is visible.
  const openChildren = openSlug ? getAllDescendants(entries, openSlug) : [];
  const openDescendantSlugs = new Set(openChildren.map((d) => d.slug));
  const visible = entries.filter((e) => !openDescendantSlugs.has(e.slug));

  let items: RenderItem[];
  if (openSlug) {
    const parentIdx = visible.findIndex((e) => e.slug === openSlug);
    if (parentIdx < 0) {
      items = visible.map((e) => ({ kind: "card", entry: e }));
    } else {
      const rowEnd = Math.min(
        Math.floor(parentIdx / cols) * cols + cols,
        visible.length,
      );
      items = [
        ...visible
          .slice(0, rowEnd)
          .map((e) => ({ kind: "card" as const, entry: e })),
        { kind: "panel" as const, parentSlug: openSlug },
        ...visible
          .slice(rowEnd)
          .map((e) => ({ kind: "card" as const, entry: e })),
      ];
    }
  } else {
    items = visible.map((e) => ({ kind: "card", entry: e }));
  }

  const openParent = openSlug
    ? entries.find((e) => e.slug === openSlug)
    : undefined;

  return (
    <div ref={gridRef} className={styles.grid}>
      {items.map((it) => {
        if (it.kind === "panel") {
          return (
            <ExpandPanel
              key={`panel-${it.parentSlug}`}
              panelId={`expand-${it.parentSlug}`}
              parentName={openParent?.name ?? ""}
              regions={openChildren}
              onClose={() => setOpenSlug(null)}
            />
          );
        }
        return (
          <DestinationCard
            key={it.entry.slug}
            entry={it.entry}
            isOpen={openSlug === it.entry.slug}
            onToggleRegions={
              it.entry.regionCount > 0
                ? () =>
                    setOpenSlug(
                      openSlug === it.entry.slug ? null : it.entry.slug,
                    )
                : undefined
            }
          />
        );
      })}
    </div>
  );
}
